/**
 * Copyright © IMPACT Commerce AB. All rights reserved.
 * See COPYING.txt for license details.
 */

import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import klaviyo from '@theme/klaviyo';
import { getVisibleRegionLabel } from '@theme/utilities';

/**
 * Back-in-stock notify-me dialog. Rendered once per page via
 * `snippets/klaviyo-bis-modal.liquid`; every <notify-me-component> opens it
 * via `openForVariant()` (variant known) or `openForSizeSelection()` (no
 * variant known yet — picks a size first, then moves to the email step).
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLFormElement} [form]
 * @property {HTMLElement} [sizeGrid]
 * @property {HTMLElement} [sizeStep]
 * @property {HTMLElement} [emailStep]
 * @property {HTMLElement} [productHeading]
 * @property {HTMLElement} [variantSubtitle]
 * @property {HTMLInputElement} [emailInput]
 * @property {HTMLInputElement} [optinCheckbox]
 * @property {HTMLButtonElement} [submitButton]
 * @property {HTMLElement} [status]
 *
 * @extends {DialogComponent}
 */
export class KlaviyoBisDialog extends DialogComponent {
  /** @type {string|null} */
  #variantId = null;

  /** @type {string} */
  #productTitle = '';

  /** @type {ReturnType<typeof setTimeout>|null} */
  #sizePickTimeout = null;

  connectedCallback() {
    super.connectedCallback();
    const { form, sizeGrid } = /** @type {Refs} */ (this.refs);
    form?.addEventListener('submit', this.#handleSubmit);
    sizeGrid?.addEventListener('click', this.#handleSizeGridClick);
    this.addEventListener(DialogCloseEvent.eventName, this.#cancelPendingSizePick);
  }

  /**
   * Show the dialog primed for a specific variant.
   * @param {{ variantId: string|number, productTitle?: string, variantTitle?: string }} args
   */
  openForVariant({ variantId, productTitle, variantTitle }) {
    this.#cancelPendingSizePick();
    this.#variantId = String(variantId);
    this.#productTitle = productTitle || '';
    this.#showEmailStep(variantTitle || '');
    this.showDialog();
    requestAnimationFrame(() => /** @type {Refs} */ (this.refs).emailInput?.focus());
  }

  /**
   * Show the dialog on its size-picker step. `sizeGrid` is a clone of the
   * triggering <notify-me-component>'s pre-rendered `<template>` — the
   * dialog itself has no product context to render one.
   * @param {{ productTitle?: string, sizeGrid: DocumentFragment|null }} args
   */
  openForSizeSelection({ productTitle, sizeGrid: newSizeGrid }) {
    this.#cancelPendingSizePick();
    this.#variantId = null;
    this.#productTitle = productTitle || '';

    const { productHeading, sizeGrid, sizeStep, emailStep } = /** @type {Refs} */ (this.refs);
    if (productHeading) productHeading.textContent = this.#productTitle;
    if (sizeGrid && newSizeGrid) sizeGrid.replaceChildren(newSizeGrid);
    sizeStep?.removeAttribute('hidden');
    emailStep?.setAttribute('hidden', '');

    this.showDialog();
  }

  /**
   * Delegated click on the size grid. Marks the pick `.is-selected` and
   * holds briefly before moving to the email step, so it's visibly
   * confirmed before the grid disappears.
   * @param {MouseEvent} event
   */
  #handleSizeGridClick = (event) => {
    const target = event.target instanceof Element ? event.target.closest('[data-variant-id]') : null;
    if (!(target instanceof HTMLElement)) return;

    const variantId = target.getAttribute('data-variant-id');
    if (!variantId) return;

    const { sizeGrid } = /** @type {Refs} */ (this.refs);
    if (sizeGrid) {
      for (const item of sizeGrid.querySelectorAll('.variant-option__grid-item')) {
        item.classList.toggle('is-selected', item === target);
        item.setAttribute('aria-selected', item === target ? 'true' : 'false');
      }
    }

    const variantTitle = getVisibleRegionLabel(target, target.getAttribute('data-variant-title') || '');
    this.#cancelPendingSizePick();
    this.#sizePickTimeout = setTimeout(() => {
      this.#sizePickTimeout = null;
      this.#variantId = variantId;
      this.#showEmailStep(variantTitle);
      requestAnimationFrame(() => /** @type {Refs} */ (this.refs).emailInput?.focus());
    }, 250);
  };

  /** Cancels a pending size-pick transition — a later open/pick, or the dialog closing, must win. */
  #cancelPendingSizePick = () => {
    if (this.#sizePickTimeout == null) return;
    clearTimeout(this.#sizePickTimeout);
    this.#sizePickTimeout = null;
  };

  /** @param {string} variantTitle */
  #showEmailStep(variantTitle) {
    const { productHeading, variantSubtitle, sizeStep, emailStep } = /** @type {Refs} */ (this.refs);
    if (productHeading) productHeading.textContent = this.#productTitle;
    if (variantSubtitle) variantSubtitle.textContent = variantTitle;
    this.#setStatus('');
    sizeStep?.setAttribute('hidden', '');
    emailStep?.removeAttribute('hidden');
  }

  /** @param {SubmitEvent} event */
  #handleSubmit = async (event) => {
    event.preventDefault();
    if (!this.#variantId) return;

    const { emailInput, optinCheckbox, form } = /** @type {Refs} */ (this.refs);
    const email = emailInput?.value.trim() ?? '';
    const optin = Boolean(optinCheckbox?.checked);

    this.#setBusy(true);
    this.#setStatus('');

    try {
      await klaviyo.createClientBackInStockSubscription({ email, variantId: this.#variantId });
      if (optin) {
        // Newsletter opt-in is best-effort — don't fail the BIS flow on it.
        try {
          await klaviyo.createClientSubscription({ email });
        } catch {
          /* swallow */
        }
      }
      this.#setStatus(this.dataset.successText || 'You are on the list!', 'success');
      form?.reset();
    } catch (err) {
      const message = err instanceof Error ? err.message : undefined;
      this.#setStatus(message || this.dataset.errorText || 'Subscription failed.', 'error');
    } finally {
      this.#setBusy(false);
    }
  };

  /** @param {boolean} busy */
  #setBusy(busy) {
    this.toggleAttribute('busy', busy);
    const { submitButton } = /** @type {Refs} */ (this.refs);
    if (submitButton) submitButton.disabled = busy;
  }

  /** @param {string} text @param {'success'|'error'|''} [variant] */
  #setStatus(text, variant = '') {
    const { status } = /** @type {Refs} */ (this.refs);
    if (!status) return;
    status.textContent = text;
    status.dataset.variant = variant;
  }
}

if (!customElements.get('klaviyo-bis-dialog')) {
  customElements.define('klaviyo-bis-dialog', KlaviyoBisDialog);
}
